import type { ComparisonConfig } from './ComparisonPage';
import { HOTELSTACK_VS_INNROAD, HOTELSTACK_VS_WEBREZPRO } from './seo-comparison-p3b.data';

/* ========================================================================== */
/*  HotelStack.io vs SiteMinder                                               */
/* ========================================================================== */

const HOTELSTACK_VS_SITEMINDER: ComparisonConfig = {
  slug: 'hotelstack-vs-siteminder',
  competitorName: 'SiteMinder',
  heroTitle: 'HotelStack.io vs SiteMinder',
  heroSubtitle: 'SiteMinder is an Australian channel manager founded in 2006 that focuses on OTA distribution. HotelStack.io delivers a complete 36-module hotel management platform with built-in channel management, PMS, F&B, revenue tools, and more — all at a lower price.',
  overviewComparison: [
    { label: 'Platform Type', hotelstack: 'All-in-One HMS (36 Modules)', competitor: 'Channel Manager + Add-Ons' },
    { label: 'Pricing', hotelstack: 'From $49/mo', competitor: 'From $99+/mo' },
    { label: 'Built-In PMS', hotelstack: 'Full PMS Included', competitor: 'Little Hotelier (Separate)' },
    { label: 'F&B / POS', hotelstack: 'Built-In', competitor: 'Not Available' },
    { label: 'Revenue Management', hotelstack: 'Advanced (Built-In)', competitor: 'Basic Yield Rules' },
    { label: 'Booking Engine', hotelstack: 'BookMyRoom.io (Free)', competitor: 'TheBookingButton (Add-On)' },
  ],
  featureTable: [
    {
      category: 'AI & Intelligence',
      features: [
        { name: 'AI Dynamic Pricing', hotelstack: true, competitor: false },
        { name: 'AI Demand Forecasting (30/60/90 day)', hotelstack: true, competitor: false },
        { name: 'AI Review Response Generator', hotelstack: true, competitor: false },
        { name: 'AI Guest Concierge (Multilingual)', hotelstack: true, competitor: false },
        { name: 'AI Sentiment Analysis', hotelstack: true, competitor: false },
        { name: 'Predictive Maintenance', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Core PMS',
      features: [
        { name: 'Visual Room Board', hotelstack: true, competitor: false },
        { name: 'Reservation Management', hotelstack: true, competitor: 'Via Little Hotelier' },
        { name: 'Night Audit Wizard', hotelstack: true, competitor: false },
        { name: 'Guest Folio Management', hotelstack: true, competitor: false },
        { name: 'Group Bookings', hotelstack: true, competitor: false },
        { name: 'Online Check-In Portal', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Distribution & Channel Management',
      features: [
        { name: 'Channel Manager (100+ OTAs)', hotelstack: true, competitor: true },
        { name: 'GDS Connectivity', hotelstack: true, competitor: true },
        { name: 'Metasearch Integration', hotelstack: true, competitor: true },
        { name: 'Rate Parity Monitoring', hotelstack: true, competitor: true },
        { name: 'Direct Booking Engine', hotelstack: true, competitor: 'Add-On' },
      ],
    },
    {
      category: 'Revenue Management',
      features: [
        { name: 'Dynamic Pricing Rules', hotelstack: true, competitor: 'Basic' },
        { name: 'What-If Revenue Simulator', hotelstack: true, competitor: false },
        { name: 'Competitor Rate Monitor', hotelstack: true, competitor: false },
        { name: 'Demand Forecasting', hotelstack: true, competitor: false },
        { name: 'RevPAR Analytics', hotelstack: true, competitor: 'Basic' },
      ],
    },
    {
      category: 'Guest & Operations',
      features: [
        { name: '360-Degree Guest Profiles', hotelstack: true, competitor: false },
        { name: 'Housekeeping Management', hotelstack: true, competitor: false },
        { name: 'POS Terminal', hotelstack: true, competitor: false },
        { name: 'Room Service Module', hotelstack: true, competitor: false },
        { name: 'Staff Scheduling', hotelstack: true, competitor: false },
        { name: 'Maintenance Work Orders', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Multi-Property & Compliance',
      features: [
        { name: 'Chain Dashboard', hotelstack: true, competitor: 'Limited' },
        { name: 'Cross-Property Analytics', hotelstack: true, competitor: false },
        { name: 'Multi-Currency Billing', hotelstack: true, competitor: 'Limited' },
        { name: 'Regional Tax Compliance', hotelstack: true, competitor: false },
        { name: 'Loyalty Program Management', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Resilience & Offline',
      features: [
        { name: 'Offline-Capable PWA', hotelstack: true, competitor: false },
        { name: 'Offline Mutation Queue', hotelstack: true, competitor: false },
        { name: 'Background Sync on Reconnect', hotelstack: true, competitor: false },
        { name: 'Conflict-Aware Reconciliation', hotelstack: true, competitor: false },
        { name: 'Installable Front Desk', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Corporate & Group Sales',
      features: [
        { name: 'Corporate Rate Management', hotelstack: true, competitor: false },
        { name: 'Group Booking with Rooming Lists', hotelstack: true, competitor: false },
        { name: 'AR Aging & Production Reports', hotelstack: true, competitor: false },
        { name: 'Dining Reservations', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Staff & HR',
      features: [
        { name: 'Staff Directory & Scheduling', hotelstack: true, competitor: false },
        { name: 'Payroll Processing', hotelstack: true, competitor: false },
        { name: 'Leave & Attendance', hotelstack: true, competitor: false },
        { name: 'Performance Analytics', hotelstack: true, competitor: false },
      ],
    },
  ],
  pricingComparison: {
    hotelstackStarting: '$49',
    competitorStarting: '$99+',
    hotelstackNote: 'All 36 modules included — PMS, channel manager, booking engine, F&B, revenue management. Regional pricing in EUR, GBP, AED, INR, THB. Free onboarding and migration.',
    competitorNote: 'Channel manager from $99+/mo. PMS (Little Hotelier) and booking engine (TheBookingButton) are separate products with separate fees. No F&B or operational modules.',
  },
  advantages: [
    'Complete all-in-one platform with 36 modules vs SiteMinder channel-manager-only approach',
    'Full PMS built in — no need to purchase Little Hotelier separately for property management',
    'F&B, POS, room service, and kitchen management included at no extra cost',
    'Advanced revenue management with dynamic pricing, forecasting, and what-if simulators',
    'Guest CRM with 360-degree profiles, loyalty programs, and automated communications',
    'Operational tools: housekeeping, maintenance, staff scheduling all integrated',
    'Lower starting price ($49 vs $99+) with significantly more functionality included',
    'Free onboarding and data migration with 24/7 dedicated support',
  ],
  verdict: {
    title: 'Our Verdict',
    summary: 'SiteMinder excels as a standalone channel manager with strong OTA connectivity built over nearly two decades. However, it is not a complete hotel management platform. HotelStack.io includes everything SiteMinder offers in distribution plus a full PMS, F&B/POS, revenue management, guest CRM, corporate sales, HR/payroll, and 29+ additional modules — all in one subscription at a lower price.',
    bestFor: [
      'Hotels wanting a single platform instead of piecing together SiteMinder + PMS + POS',
      'Properties needing full PMS, F&B, and operational tools beyond channel management',
      'Hotels wanting advanced revenue management and demand forecasting',
      'Small properties finding SiteMinder too expensive for just distribution',
      'Multi-property chains needing centralized management across all operations',
    ],
  },
  faqItems: [
    { question: 'Can I migrate from SiteMinder to HotelStack.io?', answer: 'Yes. Our migration team transfers your OTA connections, rate plans, and booking data. HotelStack.io connects to the same 100+ OTAs that SiteMinder supports. Migration is free and typically completed within 48 hours with zero downtime.' },
    { question: 'Does HotelStack.io match SiteMinder channel management capabilities?', answer: 'Yes. HotelStack.io includes a full channel manager connecting to 100+ OTAs, GDS via Amadeus and Sabre, and metasearch platforms. Rate parity monitoring and real-time inventory sync are standard features included in every plan.' },
    { question: 'Why choose HotelStack.io over SiteMinder + Little Hotelier?', answer: 'Purchasing SiteMinder (channel manager) plus Little Hotelier (PMS) plus TheBookingButton (booking engine) separately costs significantly more than HotelStack.io while still lacking F&B, revenue management, staff scheduling, corporate sales, HR/payroll, and 24+ other modules that HotelStack.io includes.' },
    { question: 'Is HotelStack.io suitable for small properties currently using SiteMinder?', answer: 'Absolutely. Our Starter plan at $49/month includes all 36 modules with no feature restrictions. Small properties get a complete management platform instead of just a channel manager, at a lower total cost than SiteMinder alone.' },
  ],
};

/* ========================================================================== */
/*  HotelStack.io vs RoomRaccoon                                              */
/* ========================================================================== */

const HOTELSTACK_VS_ROOMRACCOON: ComparisonConfig = {
  slug: 'hotelstack-vs-roomraccoon',
  competitorName: 'RoomRaccoon',
  heroTitle: 'HotelStack.io vs RoomRaccoon',
  heroSubtitle: 'RoomRaccoon is a Dutch AI-driven HMS targeting boutique and small hotels. HotelStack.io scales from boutique to enterprise with 36 modules, advanced revenue management, full F&B/POS, and multi-property support — all at competitive pricing.',
  overviewComparison: [
    { label: 'Total Modules', hotelstack: '36 Integrated Modules', competitor: '10+ Modules' },
    { label: 'Pricing', hotelstack: 'From $49/mo', competitor: 'From $79+/mo' },
    { label: 'Target Segment', hotelstack: 'SMB to Enterprise', competitor: 'Boutique / Small Hotels' },
    { label: 'F&B / POS', hotelstack: 'Built-In', competitor: 'Not Available' },
    { label: 'Multi-Property', hotelstack: 'Full Chain Management', competitor: 'Limited' },
    { label: 'Global Reach', hotelstack: '40+ Countries', competitor: 'Primarily Europe' },
  ],
  featureTable: [
    {
      category: 'AI & Intelligence',
      features: [
        { name: 'AI Dynamic Pricing', hotelstack: true, competitor: false },
        { name: 'AI Demand Forecasting (30/60/90 day)', hotelstack: true, competitor: false },
        { name: 'AI Review Response Generator', hotelstack: true, competitor: false },
        { name: 'AI Guest Concierge (Multilingual)', hotelstack: true, competitor: false },
        { name: 'AI Sentiment Analysis', hotelstack: true, competitor: false },
        { name: 'Predictive Maintenance', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Core PMS',
      features: [
        { name: 'Visual Room Board', hotelstack: true, competitor: true },
        { name: 'Reservation Management', hotelstack: true, competitor: true },
        { name: 'Night Audit Wizard', hotelstack: true, competitor: true },
        { name: 'Guest Folio Management', hotelstack: true, competitor: true },
        { name: 'Group Bookings', hotelstack: true, competitor: 'Limited' },
        { name: 'Online Check-In Portal', hotelstack: true, competitor: true },
      ],
    },
    {
      category: 'Revenue & Distribution',
      features: [
        { name: 'Channel Manager (100+ OTAs)', hotelstack: true, competitor: 'Fewer OTAs' },
        { name: 'AI-Powered Yield Management', hotelstack: true, competitor: true },
        { name: 'Dynamic Pricing Rules', hotelstack: true, competitor: 'Basic' },
        { name: 'What-If Revenue Simulator', hotelstack: true, competitor: false },
        { name: 'Competitor Rate Monitor', hotelstack: true, competitor: false },
        { name: 'Demand Forecasting', hotelstack: true, competitor: 'Basic' },
      ],
    },
    {
      category: 'Guest Management',
      features: [
        { name: '360-Degree Guest Profiles', hotelstack: true, competitor: 'Basic' },
        { name: 'Loyalty Program Management', hotelstack: true, competitor: false },
        { name: 'Guest Communication Automation', hotelstack: true, competitor: true },
        { name: 'Guest Spend Analytics', hotelstack: true, competitor: false },
        { name: 'ID Upload & Verification', hotelstack: true, competitor: true },
      ],
    },
    {
      category: 'F&B & Operations',
      features: [
        { name: 'POS Terminal', hotelstack: true, competitor: false },
        { name: 'Room Service Module', hotelstack: true, competitor: false },
        { name: 'Kitchen Display System', hotelstack: true, competitor: false },
        { name: 'Housekeeping Management', hotelstack: true, competitor: true },
        { name: 'Maintenance Work Orders', hotelstack: true, competitor: false },
        { name: 'Staff Scheduling', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Enterprise & Multi-Property',
      features: [
        { name: 'Chain Dashboard', hotelstack: true, competitor: 'Limited' },
        { name: 'Cross-Property Analytics', hotelstack: true, competitor: false },
        { name: 'Centralized Rate Management', hotelstack: true, competitor: false },
        { name: 'Brand Standards Enforcement', hotelstack: true, competitor: false },
        { name: 'Platform Super-Admin', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Resilience & Offline',
      features: [
        { name: 'Offline-Capable PWA', hotelstack: true, competitor: false },
        { name: 'Offline Mutation Queue', hotelstack: true, competitor: false },
        { name: 'Background Sync on Reconnect', hotelstack: true, competitor: false },
        { name: 'Conflict-Aware Reconciliation', hotelstack: true, competitor: false },
        { name: 'Installable Front Desk', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Corporate & Group Sales',
      features: [
        { name: 'Corporate Rate Management', hotelstack: true, competitor: false },
        { name: 'Group Booking with Rooming Lists', hotelstack: true, competitor: 'Limited' },
        { name: 'AR Aging & Production Reports', hotelstack: true, competitor: false },
        { name: 'Dining Reservations', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Staff & HR',
      features: [
        { name: 'Staff Directory & Scheduling', hotelstack: true, competitor: false },
        { name: 'Payroll Processing', hotelstack: true, competitor: false },
        { name: 'Leave & Attendance', hotelstack: true, competitor: false },
        { name: 'Performance Analytics', hotelstack: true, competitor: false },
      ],
    },
  ],
  pricingComparison: {
    hotelstackStarting: '$49',
    competitorStarting: '$79+',
    hotelstackNote: 'All 36 modules included. Scales from 10-room boutique to 500+ room enterprise. Regional pricing in EUR, GBP, AED, INR, THB. Free onboarding and migration.',
    competitorNote: 'Tiered pricing from EUR 79+/mo. Advanced features locked behind higher tiers. Limited enterprise and multi-property capabilities.',
  },
  advantages: [
    'Over three times the modules (36 vs 10+) with no features locked behind premium tiers',
    'Scales from boutique to enterprise — RoomRaccoon is limited to small/mid properties',
    'Complete F&B module with POS, room service, and kitchen management included',
    'Full multi-property chain management with centralized analytics and rate distribution',
    'Advanced revenue management beyond basic AI yield — includes forecasting and simulators',
    'Loyalty program management for guest retention and repeat booking growth',
    'Global reach across 40+ countries vs RoomRaccoon European focus',
    'Staff management with scheduling, payroll, and performance tracking included',
  ],
  verdict: {
    title: 'Our Verdict',
    summary: 'RoomRaccoon is a solid choice for small European boutique hotels looking for an AI-powered PMS with a modern interface. However, it lacks the depth, scalability, and operational breadth that growing properties need. HotelStack.io delivers everything RoomRaccoon offers plus F&B/POS, enterprise multi-property management, advanced revenue tools, and global compliance — making it the stronger long-term investment.',
    bestFor: [
      'Boutique hotels wanting enterprise-grade features without enterprise pricing',
      'Properties needing F&B, POS, and kitchen management in their HMS',
      'Hotels planning to scale from single property to multi-property chains',
      'Properties outside Europe needing global compliance and distribution',
      'Hotels wanting advanced revenue management beyond basic yield optimization',
    ],
  },
  faqItems: [
    { question: 'Can I migrate from RoomRaccoon to HotelStack.io?', answer: 'Yes. Our migration team handles complete data transfer from RoomRaccoon including guest profiles, reservation history, rate plans, and room configurations. Migration is free and typically completed within 48 hours with zero downtime.' },
    { question: 'Does HotelStack.io have AI-powered features like RoomRaccoon?', answer: 'Yes. HotelStack.io includes intelligent yield management, demand forecasting, dynamic pricing rules, and revenue simulators. Our AI-driven tools go beyond basic yield optimization to include competitor monitoring and what-if scenario planning.' },
    { question: 'Is HotelStack.io suitable for boutique hotels like RoomRaccoon?', answer: 'Absolutely. Our Starter plan is designed for properties of all sizes including boutique hotels. You get all 36 modules from day one with no feature restrictions, and the modern dark luxury UI provides an elegant experience that boutique properties appreciate.' },
    { question: 'How does HotelStack.io handle multi-property management vs RoomRaccoon?', answer: 'HotelStack.io includes a full chain management dashboard with cross-property analytics, centralized rate distribution, brand standards, and consolidated reporting. RoomRaccoon has limited multi-property support, making HotelStack.io the better choice for growing hotel groups.' },
  ],
};


/* ========================================================================== */
/*  Export maps                                                                */
/* ========================================================================== */

export const COMPARISON_CONFIGS_P3: Record<string, ComparisonConfig> = {
  'hotelstack-vs-siteminder': HOTELSTACK_VS_SITEMINDER,
  'hotelstack-vs-roomraccoon': HOTELSTACK_VS_ROOMRACCOON,
  'hotelstack-vs-innroad': HOTELSTACK_VS_INNROAD,
  'hotelstack-vs-webrezpro': HOTELSTACK_VS_WEBREZPRO,
};

export const COMPARISON_FAQ_P3: Record<string, { question: string; answer: string }[]> = {
  'hotelstack-vs-siteminder': HOTELSTACK_VS_SITEMINDER.faqItems,
  'hotelstack-vs-roomraccoon': HOTELSTACK_VS_ROOMRACCOON.faqItems,
  'hotelstack-vs-innroad': HOTELSTACK_VS_INNROAD.faqItems,
  'hotelstack-vs-webrezpro': HOTELSTACK_VS_WEBREZPRO.faqItems,
};
